import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Logo1 } from '../../Assests/Assests';
import './FloatingLogo.css';

const HERO_ID = 'home-hero';
const CORNER_RIGHT = 20;
const CORNER_TOP = 80; // below navbar
const LOGO_HERO_SIZE = 90;
const LOGO_CORNER_SIZE = 48;

const FloatingLogo = () => {
  const [inHero, setInHero] = useState(false);
  const [dragging, setDragging] = useState(false);
  // position as {x, y} in px from top-left of viewport (used only in hero mode)
  const [heroPos, setHeroPos] = useState({ x: null, y: null });
  const dragOffset = useRef({ dx: 0, dy: 0 });
  const logoRef = useRef(null);

  const [spinDur, setSpinDur] = useState(7);
  const lastScrollRef = useRef(window.scrollY);
  const lastTimeRef = useRef(performance.now());
  const spinDurRef = useRef(7);
  const animRef = useRef(null);

  // Detect if hero section is in viewport
  useEffect(() => {
    const check = () => {
      const hero = document.getElementById(HERO_ID);
      if (!hero) { setInHero(false); return; }
      const rect = hero.getBoundingClientRect();
      setInHero(rect.bottom > 80);
    };
    check();
    window.addEventListener('scroll', check, { passive: true });
    return () => window.removeEventListener('scroll', check);
  }, []);

  // Initialize hero position to center of viewport
  useEffect(() => {
    if (heroPos.x === null) {
      setHeroPos({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
    }
  }, []);

  // Scroll speed → spin speed
  useEffect(() => {
    const onScroll = () => {
      const now = performance.now();
      const vel = Math.abs(window.scrollY - lastScrollRef.current) / (now - lastTimeRef.current) * 16;
      const dur = Math.max(0.4, 7 - vel * 3);
      spinDurRef.current = dur;
      setSpinDur(dur);
      lastScrollRef.current = window.scrollY;
      lastTimeRef.current = now;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Decelerate back to resting speed
  useEffect(() => {
    const decel = () => {
      if (spinDurRef.current < 7) {
        spinDurRef.current = Math.min(7, spinDurRef.current + 0.07);
        setSpinDur(spinDurRef.current);
      }
      animRef.current = requestAnimationFrame(decel);
    };
    animRef.current = requestAnimationFrame(decel);
    return () => cancelAnimationFrame(animRef.current);
  }, []);

  // Mouse drag
  const onMouseDown = useCallback((e) => {
    if (!inHero) return;
    e.preventDefault();
    setDragging(true);
    dragOffset.current = {
      dx: e.clientX - heroPos.x,
      dy: e.clientY - heroPos.y,
    };
  }, [inHero, heroPos]);

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      setHeroPos({
        x: Math.max(50, Math.min(window.innerWidth - 50, e.clientX - dragOffset.current.dx)),
        y: Math.max(50, Math.min(window.innerHeight - 50, e.clientY - dragOffset.current.dy)),
      });
    };
    const onUp = () => setDragging(false);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [dragging]);

  // Touch drag
  const onTouchStart = useCallback((e) => {
    if (!inHero) return;
    const t = e.touches[0];
    setDragging(true);
    dragOffset.current = { dx: t.clientX - heroPos.x, dy: t.clientY - heroPos.y };
  }, [inHero, heroPos]);

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      const t = e.touches[0];
      setHeroPos({
        x: Math.max(50, Math.min(window.innerWidth - 50, t.clientX - dragOffset.current.dx)),
        y: Math.max(50, Math.min(window.innerHeight - 50, t.clientY - dragOffset.current.dy)),
      });
    };
    const onEnd = () => setDragging(false);
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd);
    return () => {
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
    };
  }, [dragging]);

  const size = inHero ? LOGO_HERO_SIZE : LOGO_CORNER_SIZE;

  const style = inHero
    ? {
        position: 'fixed',
        left: heroPos.x,
        top: heroPos.y,
        transform: 'translate(-50%, -50%)',
        cursor: dragging ? 'grabbing' : 'grab',
        width: size,
        height: size,
        animationDuration: `${spinDur}s`,
        zIndex: 999,
      }
    : {
        position: 'fixed',
        right: CORNER_RIGHT,
        top: CORNER_TOP,
        width: size,
        height: size,
        animationDuration: `${spinDur}s`,
        zIndex: 999,
        cursor: 'default',
      };

  return (
    <div
      ref={logoRef}
      className={`floating-logo ${inHero ? 'floating-logo--hero' : 'floating-logo--corner'}`}
      style={style}
      onMouseDown={onMouseDown}
      onTouchStart={onTouchStart}
    >
      <img
        src={Logo1}
        alt="Riymay"
        className="floating-logo__img"
        draggable={false}
      />
      {inHero && (
        <span className="floating-logo__hint">drag anywhere</span>
      )}
    </div>
  );
};

export default FloatingLogo;
